import translate from '@/utils/translate';
import {
  MAX_SOURCE_DIMENSION_PX,
  MAX_SOURCE_DURATION_SECONDS,
} from '@/types/postProductionGeneration';

export type VideoMetadata = {
  durationSeconds: number;
  width: number;
  height: number;
};

export type ResizeEligibility =
  | {ok: true; meta?: VideoMetadata}
  | {ok: false; reason: string};

export function evaluateResizeEligibility(
  meta: VideoMetadata
): ResizeEligibility {
  if (meta.durationSeconds > MAX_SOURCE_DURATION_SECONDS) {
    return {
      ok: false,
      reason: translate(
        'This video is longer than {max} seconds, which is the maximum LTX can resize.',
        {max: MAX_SOURCE_DURATION_SECONDS}
      ),
    };
  }
  const longestSide = Math.max(meta.width, meta.height);
  if (longestSide > MAX_SOURCE_DIMENSION_PX) {
    return {
      ok: false,
      reason: translate(
        'This video is {width}×{height}. LTX can resize videos up to {max}px on the longest side.',
        {width: meta.width, height: meta.height, max: MAX_SOURCE_DIMENSION_PX}
      ),
    };
  }
  return {ok: true, meta};
}

const PROBE_TIMEOUT_MS = 10000;

export function probeVideoMetadata(url: string): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    const cleanup = () => {
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.load();
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('Timed out reading video metadata'));
    }, PROBE_TIMEOUT_MS);

    video.onloadedmetadata = () => {
      const meta: VideoMetadata = {
        durationSeconds: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      };
      cleanup();
      resolve(meta);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('Failed to load video metadata'));
    };

    video.src = url;
  });
}

export async function checkResizeEligibility(
  url: string | undefined | null
): Promise<ResizeEligibility> {
  if (!url) return {ok: true};
  try {
    const meta = await probeVideoMetadata(url);
    if (!Number.isFinite(meta.durationSeconds) || meta.width === 0) {
      return {ok: true};
    }
    return evaluateResizeEligibility(meta);
  } catch {
    return {ok: true};
  }
}
